import { useMutation, useQueryClient } from "@tanstack/react-query";
import { qk } from "@/lib/query-keys";
import { toastAdvertencia, toastErrorApi, toastExito } from "@/lib/toast";
import {
  crearProveedor,
  darDeBajaProveedor,
  editarProveedor,
  importarProveedores,
  reactivarProveedor,
  type CrearProveedorInput,
  type EditarProveedorInput,
  type FilaImportacionProveedor,
  type ResultadoImportacionProveedores,
} from "../services/proveedores-service";

export function useCrearProveedor(opts?: { onCreado?: () => void }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CrearProveedorInput) => crearProveedor(input),
    onSuccess: (p) => {
      toastExito({ title: `Proveedor #${p.numeroProveedor} creado`, message: "Disponible en el catálogo." });
      void qc.invalidateQueries({ queryKey: qk.adminProveedores });
      opts?.onCreado?.();
    },
    onError: toastErrorApi,
  });
}

export function useEditarProveedor(opts?: { onEditado?: () => void }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ numero, input }: { numero: number; input: EditarProveedorInput }) => editarProveedor(numero, input),
    onSuccess: () => {
      toastExito({ title: "Proveedor actualizado", message: "Cambios guardados." });
      void qc.invalidateQueries({ queryKey: qk.adminProveedores });
      opts?.onEditado?.();
    },
    onError: toastErrorApi,
  });
}

export function useDarDeBajaProveedor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ numero }: { numero: number }) => darDeBajaProveedor(numero),
    onSuccess: (_data, vars) => {
      toastExito({ title: `Proveedor #${vars.numero} dado de baja`, message: "Quedó inactivo." });
      void qc.invalidateQueries({ queryKey: qk.adminProveedores });
    },
    onError: toastErrorApi,
  });
}

export function useReactivarProveedor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ numero }: { numero: number }) => reactivarProveedor(numero),
    onSuccess: (_data, vars) => {
      toastExito({ title: `Proveedor #${vars.numero} reactivado`, message: "Disponible para nuevas solicitudes." });
      void qc.invalidateQueries({ queryKey: qk.adminProveedores });
    },
    onError: toastErrorApi,
  });
}

/** Toast con el resumen de una importación (creados/actualizados/omitidos + errores). */
function reportarImportacion(res: ResultadoImportacionProveedores) {
  const resumen = `${res.creados} creados · ${res.actualizados} actualizados · ${res.omitidos} omitidos`;
  if (res.errores.length > 0) {
    const muestra = res.errores.slice(0, 5).join(" · ");
    const extra = res.errores.length > 5 ? ` (+${res.errores.length - 5} más)` : "";
    toastAdvertencia({ title: "Importación de proveedores con avisos", message: `${resumen}. ${muestra}${extra}` });
  } else {
    toastExito({ title: "Importación de proveedores completada", message: resumen });
  }
}

export function useImportarProveedores(opts?: { onImportado?: () => void }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (filas: FilaImportacionProveedor[]) => importarProveedores(filas),
    onSuccess: (res) => {
      reportarImportacion(res);
      void qc.invalidateQueries({ queryKey: qk.adminProveedores });
      opts?.onImportado?.();
    },
    onError: toastErrorApi,
  });
}
